"use client";

import { ClipboardCheck } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { useScopedI18n } from "@/locales/client";
import { useListPracticeTests } from "../api/use-list-practice-tests";
import {
  sourceBasePath,
  sourceInput,
  type TestSource,
} from "../hooks/test-source";

const VISIBLE_ATTEMPTS = 3;

export function PracticeTestHistory({
  source,
  showEmptyState = false,
}: {
  source: TestSource;
  showEmptyState?: boolean;
}) {
  const t = useScopedI18n("practiceTest");
  const router = useRouter();
  const [showAll, setShowAll] = useState(false);
  const { data: attempts } = useListPracticeTests(sourceInput(source));

  if (!attempts || attempts.length === 0) {
    if (!showEmptyState) return null;
    return (
      <div className="flex flex-col items-center justify-center gap-1.5 rounded-2xl border border-dashed px-6 py-12 text-center">
        <ClipboardCheck className="size-6 text-muted-foreground" />
        <p className="text-sm font-medium">{t("setup.noAttempts")}</p>
        <p className="text-sm text-muted-foreground">
          {t("setup.noAttemptsHint")}
        </p>
      </div>
    );
  }

  const visible = showAll ? attempts : attempts.slice(0, VISIBLE_ATTEMPTS);
  const hiddenCount = attempts.length - VISIBLE_ATTEMPTS;

  const statusLabel = (status: string) => {
    if (status === "generating") return t("setup.building");
    if (status === "failed") return t("setup.failed");
    return t("setup.inProgress");
  };

  return (
    <div className="flex flex-col gap-4">
      <h4 className="font-semibold text-base">{t("setup.pastAttempts")}</h4>
      <div className="flex flex-col gap-2">
        {visible.map((attempt) => {
          const isCompleted = attempt.status === "completed";
          const percent =
            attempt.gradedCount > 0
              ? Math.round((attempt.correctCount / attempt.gradedCount) * 100)
              : 0;

          return (
            <Card
              key={attempt.id}
              className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() =>
                router.push(`${sourceBasePath(source)}/test/${attempt.id}`)
              }
            >
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">
                  {new Date(attempt.createdAt).toLocaleDateString()}
                </span>
                <span className="text-xs text-muted-foreground">
                  {/* @ts-expect-error */}
                  {t("ready.questions", { count: attempt.questionCount })}
                </span>
              </div>
              {isCompleted ? (
                <div className="flex items-center gap-2 tabular-nums">
                  <span className="text-sm font-medium">
                    {attempt.correctCount}/{attempt.gradedCount}
                  </span>
                  <span className="text-sm font-semibold text-brand-primary">
                    {percent}%
                  </span>
                </div>
              ) : (
                <span className="text-xs font-medium text-muted-foreground bg-muted rounded-full px-2 py-1">
                  {statusLabel(attempt.status)}
                </span>
              )}
            </Card>
          );
        })}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setShowAll((s) => !s)}
            className="w-fit text-sm font-medium text-brand-primary hover:underline"
          >
            {showAll
              ? t("setup.showFewer")
              : // @ts-expect-error
                t("setup.showAllAttempts", { count: hiddenCount })}
          </button>
        )}
      </div>
    </div>
  );
}
